import { useEffect, useState } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { capitalizeFirstLetter } from "../../helpers";
import moment from "moment";

const MatchingInventories = ({
  apiData,
  handleClose,
  loading,
  onView,
  onDeal,
  navigate
}) => {
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Container style={{ height: `${height}px`, overflow: "auto" }}>

      {/* 🔹 Header */}
      <Header>
        <h1 className="text-white text-lg font-medium">
          Matching Properties
        </h1>
        <button onClick={handleClose}>
          <MdClose color="white" size={24} />
        </button>
      </Header>

      {/* Content */}
      <Content>

        {loading ? (
          // Loader State
          <div className="flex flex-col items-center justify-center py-16">

            <div className="w-8 h-8 border-2 border-gray-200 border-t-primary-color rounded-full animate-spin mb-3"></div>

            <p className="text-sm text-gray-500">
              Finding best matches...
            </p>
          </div>
        )
          :
          <>
            {apiData?.length > 0 ? (
              apiData.map((inventory, index) => {
                return (
                  <PropertyCard key={inventory.id || index}>

                    <CardHeader>
                      <PropertyInfo>
                        <InventoryName>
                          {capitalizeFirstLetter(inventory.inventoryName)}
                        </InventoryName>
                        <InventoryType>
                          {inventory.address}
                        </InventoryType>
                      </PropertyInfo>

                      {/* Keep same style as rent */}
                      {/* <Value className="font-medium text-green-600">
                        ₹{inventory.rent || 0}
                      </Value> */}
                      <Value className="font-semibold text-primary-color text-sm bg-primary-color/10 px-3 py-1 rounded-full">
                        ₹{inventory.rent || 0}
                      </Value>
                    </CardHeader>

                    <Divider />

                    <PropertyDetails>

                      <DetailRow>
                        <DetailItem>
                          <Label>Match</Label>
                          {/* <Value>
                            {inventory.matchScore || 0}%
                          </Value> */}
                          <Value className={`text-xs font-semibold px-2 py-1 rounded-full ${inventory.matchScore > 70 ? "bg-green-100 text-green-600" :
                            inventory.matchScore > 40 ? "bg-yellow-100 text-yellow-600" :
                              "bg-red-100 text-red-500"}`}>
                            {inventory.matchScore || 0}%
                          </Value>
                        </DetailItem>

                        <DetailItem>
                          <Label>Deposit</Label>
                          <Value>
                            ₹{inventory.deposit || 0}
                          </Value>
                        </DetailItem>
                      </DetailRow>

                      {/* Budget Fit */}
                      <DetailRow>
                        {/* <DetailItem>
                          <Label>Budget Fit</Label>
                          <Value className={`${budgetFit.color} font-medium`}>
                            {budgetFit.label}
                          </Value>
                        </DetailItem> */}

                        <DetailItem>
                          <Label>Demand</Label>
                          <Value className="text-xs">
                            {inventory.dealCount > 0
                              ? `🔥 ${inventory.dealCount} deals`
                              : "Low demand"}
                          </Value>
                        </DetailItem>
                      </DetailRow>

                      {/* Reuse your layout (no new styling) */}
                      <MetaInfo className="flex justify-between items-center mt-3">

                        <span className="text-xs text-gray-400">
                          Updated: {moment(inventory.updatedAt).format("DD MMM YY")}
                        </span>

                        <div className="flex gap-2">

                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // prevent parent click issues
                              onView && onView(inventory);
                            }}
                            className="text-xs px-3 py-1 bg-gray-100 rounded-full"
                          >
                            View
                          </button>
                          {inventory.dealCount === 0 &&
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // important inside modal/cards
                                onDeal && onDeal(inventory);
                              }}
                              className="
                                text-xs 
                                px-4 py-1.5 
                                bg-gradient-to-r from-primary-color to-teal-400 
                                text-white 
                                rounded-full 
                                shadow-sm 
                                hover:shadow-md 
                                hover:scale-105 
                                transition-all
                              ">
                              + Add Deal
                            </button>
                          }
                        </div>

                      </MetaInfo>

                    </PropertyDetails>

                  </PropertyCard>
                );
              })
            ) : (
              <EmptyState className="flex flex-col items-center justify-center h-full text-center px-6">

                {/* Icon */}
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <svg
                    className="w-7 h-7 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M3 10l9-7 9 7v9a2 2 0 01-2 2h-4m-6 0H5a2 2 0 01-2-2v-9z" />
                  </svg>
                </div>

                {/* Title */}
                <p className="text-sm font-medium text-gray-700">
                  No matching properties found
                </p>

                {/* Subtitle */}
                <p className="text-xs text-gray-400 mt-1 max-w-[220px]">
                  Try adjusting enquiry preferences or add new inventory to get better matches
                </p>

                {/* CTA */}
                <div className="flex gap-2 mt-5">

                  {/* <button
                    className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition"
                  >
                    Edit Enquiry
                  </button> */}

                  <button
                    onClick={() => navigate("/inventory/operational")}
                    className="text-xs px-3 py-1.5 bg-primary-color text-white rounded-full hover:opacity-90 transition"
                  >
                    + Add Inventory
                  </button>

                </div>

              </EmptyState>
            )}
          </>
        }

      </Content>
    </Container>
  );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
//const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;

const Header = tw.div`
  flex items-center justify-between 
  bg-gradient-to-r from-primary-color to-teal-400 
  py-4 px-5 
  shadow-md
`;
//const Content = tw.div`px-4 py-4 space-y-4 overflow-y-auto flex-1`;
const Content = tw.div`px-5 py-5 space-y-5 overflow-y-auto flex-1`;
//const PropertyCard = tw.div`bg-white border border-gray-200 rounded-lg p-4`;

const PropertyCard = tw.div`
  bg-white 
  rounded-2xl 
  p-5 
  border border-gray-100 
  shadow-sm 
  hover:shadow-lg 
  hover:-translate-y-1 
  transition-all duration-200
`;
const CardHeader = tw.div`flex justify-between items-start mb-3`;
const PropertyInfo = tw.div`flex flex-col`;
//const InventoryName = tw.div`text-base font-medium text-gray-800`;
//const InventoryType = tw.div`text-sm text-gray-400`;
const InventoryName = tw.div`
  text-lg font-semibold text-gray-900 tracking-tight
`;

const InventoryType = tw.div`
  text-xs text-gray-400 mt-0.5
`;
//const Divider = tw.div`h-px bg-gray-100 my-3`;
const Divider = tw.div`h-px bg-gray-100/70 my-3`;
const PropertyDetails = tw.div`space-y-3`;
const DetailRow = tw.div`grid grid-cols-2 gap-4`;
const DetailItem = tw.div`flex flex-col`;
//const Label = tw.label`text-sm text-gray-400`;
const Label = tw.label`
  text-xs text-gray-400 uppercase tracking-wide
`;
const Value = tw.span`text-gray-600 text-sm`;
const MetaInfo = tw.div`flex justify-end mt-2`;

const EmptyState = tw.div`text-center text-gray-400 text-sm py-10`;

export default MatchingInventories;